/**
 * 서버가 준 시각을 **한국 시간으로** 그린다.
 *
 * ## 왜 필요한가
 *
 * 서버의 DB 는 UTC 다(2026-09-22 개발·운영 파드에서 직접 확인: `NOW() == UTC_TIMESTAMP()`).
 * 예전에는 응답이 `2026-09-22T12:05:35` 처럼 **시간대 없이** 나갔고, 화면은 그걸
 * `.slice(0, 10)` 으로 잘라 날짜만 썼다. 그래서 **UTC 날짜가 그대로 보였다** —
 * 한국시간 오전 0~9시에 가입한 사람은 **전날로** 표시됐다.
 *
 * 이제 서버가 `+00:00` 을 붙여 보낸다. 여기서 `Asia/Seoul` 로 바꿔 그린다.
 *
 * ## 브라우저 시간대에 기대지 않는다
 *
 * `toLocaleString()` 을 그냥 부르면 **보는 사람의 기기 시간대**를 따른다. 해외에
 * 있거나 기기 시간대가 잘못 잡힌 사람에게는 다른 값이 보이고, 그 화면을 근거로
 * 이야기하면 서로 다른 시각을 말하게 된다. 한국 서비스의 운영 화면이므로
 * **`Asia/Seoul` 로 고정**한다.
 */
const KST = "Asia/Seoul";

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `2026-09-22` (KST 기준 날짜). 값이 없거나 이상하면 빈 문자열. */
export function formatKstDate(value: string | null | undefined): string {
  const date = parse(value);
  if (!date) return "";
  // `sv-SE` 로케일이 ISO 모양(`YYYY-MM-DD`)을 준다. `ko-KR` 은 «2026. 9. 22.» 라
  // 자릿수가 들쭉날쭉해서 표에서 줄이 안 맞는다.
  return new Intl.DateTimeFormat("sv-SE", { timeZone: KST }).format(date);
}

/** `2026-09-22 21:27` (KST). 값이 없거나 이상하면 빈 문자열. */
export function formatKstDateTime(value: string | null | undefined): string {
  const date = parse(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: KST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}
